import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { RequestEventType } from '@vendasta/customer-voice-service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Observable, combineLatest as observableCombineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  NO_LISTING_BUILDER_ERROR,
  NO_PREFERRED_SITES_SELECTED_ERROR,
  ReviewRequestType,
  TOO_MANY_CUSTOMER_SELECTED_FOR_SMS,
  WOULD_GO_OVER_MONTHLY_LIMIT_FOR_SMS,
} from '../../common/constants';
import { Edition } from '../../common/editions';
import { ApplicationRoutes } from '../../common/routes';
import { AppConfigService } from '../../core/app-config.service';
import { CustomerVoiceRouterService } from '../../core/cv-router.service';
import { EmailUsagesService } from '../../core/email-usages.service';
import { Template } from '../../core/template-listing.service';
import { WhiteLabelService } from '../../core/white-label.service';
import { ExpressCtaDialogService } from '../../navigable-app/express-cta-dialog/express-cta-dialog.service';
import { SendEmailService } from '../../preview-send-email/send-email.service';
import { SendSmsService } from '../../preview-send-sms/send-sms.service';
import { ReviewRequestEventService } from '../../review-request-event/review-request-event.service';
import { Customer } from '../customer-common';
import { CustomerSelectionService } from '../customer-selection.service';
import { CustomerService } from '../customers.service';

@Component({
  selector: 'app-send-review-request',
  templateUrl: './send-review-request.component.html',
  styleUrls: ['./send-review-request.component.scss'],
  standalone: false,
})
export class SendReviewRequestComponent implements OnInit {
  selectedTemplate: Template;
  sendingInProgress: boolean;
  accountGroupId: string;
  reachedExpressEmailLimit$: Observable<boolean>;
  canRender = false;
  clickTrackingLabel = 'email-limit-reached-preview-express-cta';
  appName$: Observable<string>;
  private isPaidVersion: boolean;

  constructor(
    private customerSelectionService: CustomerSelectionService,
    private sendEmailService: SendEmailService,
    private sendSmsService: SendSmsService,
    private snackbarService: SnackbarService,
    private customerVoiceRouter: CustomerVoiceRouterService,
    private translateService: TranslateService,
    private customerService: CustomerService,
    private appConfigService: AppConfigService,
    private emailUsageService: EmailUsagesService,
    private dialog: MatDialog,
    private ctaDialogService: ExpressCtaDialogService,
    private snowplowService: ProductAnalyticsService,
    private reviewRequestEventService: ReviewRequestEventService,
    private whiteLabelService: WhiteLabelService,
  ) {}

  get nbCustomerSelected(): number {
    return this.customerSelectionService.nbSelected();
  }

  get nbCustomersMissing(): number {
    if (this.selectedTemplate) {
      if (this.selectedTemplate.templateType === ReviewRequestType.email) {
        return this.customerSelectionService.nbSelected() - this.customerSelectionService.nbSelectedWithEmail();
      } else if (this.selectedTemplate.templateType === ReviewRequestType.sms) {
        return this.customerSelectionService.nbSelected() - this.customerSelectionService.nbSelectedWithPhone();
      }
    }
  }

  get customerMissingFieldText(): Observable<string> {
    return this.translateService.get('CUSTOMERS.PREVIEW_EMAIL.CUSTOMER_MISSING', {
      missing: this.nbCustomersMissing,
      selected: this.nbCustomerSelected,
      type: this.selectedTemplate.templateType === ReviewRequestType.email ? 'email' : 'phone',
    });
  }

  get customersSelectedText(): Observable<string> {
    if (this.selectedTemplate) {
      if (this.selectedTemplate.templateType === ReviewRequestType.email) {
        return this.translateService.get('CUSTOMERS.PREVIEW_EMAIL.SEND_EMAILS', {
          number: this.customerSelectionService.nbSelectedWithEmail(),
        });
      } else if (this.selectedTemplate.templateType === ReviewRequestType.sms) {
        return this.translateService.get('CUSTOMERS.PREVIEW_EMAIL.SEND_EMAILS', {
          number: this.customerSelectionService.nbSelectedWithPhone(),
        });
      }
    }
  }

  get isButtonDisabled(): boolean {
    if (this.selectedTemplate && this.canRender) {
      if (this.selectedTemplate.templateType === ReviewRequestType.email) {
        return this.customerSelectionService.nbSelectedWithEmail() < 1;
      } else if (this.selectedTemplate.templateType === ReviewRequestType.sms) {
        return this.customerSelectionService.nbSelectedWithPhone() < 1 || this.nonPaidBulkSms;
      }
    }
    return true;
  }

  get nonPaidBulkSms(): boolean {
    return (
      this.selectedTemplate &&
      this.selectedTemplate.templateType === ReviewRequestType.sms &&
      this.customerSelectionService.nbSelectedWithPhone() > 1 &&
      !this.isPaidVersion
    );
  }

  get hasNoCustomerWithEmail(): boolean {
    return this.customerSelectionService.getSelectedCustomersWithEmail().length === 0;
  }

  get hasNoCustomerWithSms(): boolean {
    return this.customerSelectionService.getSelectedCustomersWithPhone().length === 0;
  }

  ngOnInit(): void {
    this.customerVoiceRouter.accountGroupId.subscribe((agid) => {
      this.accountGroupId = agid;
    });
    this.appConfigService.appConfig.subscribe((config) => {
      this.isPaidVersion = config.isPaidVersion;
    });

    this.reachedExpressEmailLimit$ = observableCombineLatest([
      this.appConfigService.appConfig,
      this.emailUsageService.emailUsages$,
    ]).pipe(
      map(([config, usages]) => {
        return config.edition === Edition.Express && usages.monthlyUsage >= usages.monthlyLimit;
      }),
    );

    this.appName$ = this.whiteLabelService.getAppName$();
  }

  onTemplateSelect(template: Template): void {
    this.selectedTemplate = template;
  }

  canTemplateRender(canTemplateRender: boolean): void {
    this.canRender = canTemplateRender;
  }

  cancel(): void {
    this.customerVoiceRouter.navigateByUrl(ApplicationRoutes.customers);
  }

  openUpgradeDialog(): void {
    this.ctaDialogService.open(
      'https://vstatic-prod.apigateway.co/customer-voice-client.9223372035073077600.prod/assets/images/express_cta/unlimited_emails.png',
      'EXPRESS_CTA_DIALOG.IMAGE_TEXT.EMAIL_USAGE',
      `${this.clickTrackingLabel}-upgraded`,
    );
  }

  send(): void {
    this.sendingInProgress = true;
    let templateIdToSend: string;
    let customersToSend: Customer[];

    if (this.selectedTemplate.templateType === ReviewRequestType.email) {
      templateIdToSend = this.constructFullTemplateIdForEmail(this.selectedTemplate.templateId);
      customersToSend = this.customerSelectionService.getSelectedCustomersWithEmail();

      this.sendEmailService.sendReviewRequestEmail(customersToSend, templateIdToSend).subscribe({
        next: () => {
          this.customerService.customersRecentlySentEmail(this.customerSelectionService.getSelectedCustomers());
          this.customerVoiceRouter.navigateByUrl(ApplicationRoutes.customers);
          this.reviewRequestEventService
            .createReviewRequestEventFromCustomers(customersToSend, RequestEventType.REQUEST_EVENT_TYPE_EMAIL)
            .subscribe();
          this.snackbarService.successSnack(
            this.translateService.instant('CUSTOMERS.PREVIEW_EMAIL.EMAIL_SEND_SUCCESS'),
          );
        },
        error: (response: HttpErrorResponse) => {
          this.sendingInProgress = false;
          if (response.status === 412) {
            if (response.error.message === NO_PREFERRED_SITES_SELECTED_ERROR) {
              // No preferred sites are selected for the external workflow
              this.sendEmailService.openNoPreferredSitesDialog();
            } else if (response.error.message === NO_LISTING_BUILDER_ERROR) {
              this.sendEmailService.openNoListingBuilderDialog();
            }
          } else {
            this.snackbarService.errorSnack(
              this.translateService.instant('ADD_CUSTOMER_FORM.SOMETHING_WENT_WRONG_SENDING_EMAIL'),
            );
          }
        },
      });
    } else if (this.selectedTemplate.templateType === ReviewRequestType.sms) {
      templateIdToSend = this.selectedTemplate.templateId;
      customersToSend = this.customerSelectionService.getSelectedCustomersWithPhone();

      this.sendSmsService.sendReviewRequestSms(customersToSend, templateIdToSend).subscribe({
        next: () => {
          this.customerService.customersRecentlySentSms(this.customerSelectionService.getSelectedCustomers());
          this.customerVoiceRouter.navigateByUrl(ApplicationRoutes.customers);
          this.reviewRequestEventService
            .createReviewRequestEventFromCustomers(customersToSend, RequestEventType.REQUEST_EVENT_TYPE_SMS)
            .subscribe();
          this.snackbarService.successSnack(this.translateService.instant('CUSTOMERS.PREVIEW_EMAIL.SMS_SEND_SUCCESS'));
        },
        error: (error: HttpErrorResponse) => {
          this.sendingInProgress = false;
          if (error.status === 412) {
            if (error.error.message === NO_PREFERRED_SITES_SELECTED_ERROR) {
              this.sendSmsService.openNoPreferredSitesDialog();
            } else if (error.error.message === TOO_MANY_CUSTOMER_SELECTED_FOR_SMS) {
              this.snackbarService.errorSnack(
                this.translateService.instant('ADD_CUSTOMER_FORM.MAP_CSV.SMS_DAILY_LIMIT_ERROR'),
              );
            } else if (error.error.message === WOULD_GO_OVER_MONTHLY_LIMIT_FOR_SMS) {
              this.snackbarService.errorSnack(
                this.translateService.instant('ADD_CUSTOMER_FORM.MAP_CSV.SMS_MONTHLY_LIMIT_ERROR'),
              );
            } else {
              this.snackbarService.errorSnack(error.error.message);
            }
          } else {
            this.snackbarService.errorSnack(
              this.translateService.instant('ADD_CUSTOMER_FORM.SOMETHING_WENT_WRONG_SENDING_SMS'),
            );
          }
        },
      });
    }
    this.customerSelectionService.clearSelection();
  }

  constructFullTemplateIdForEmail(id: string): string {
    return `/account-group-id/${this.accountGroupId}/type/email/template-id/${id}`;
  }
}
